const TRANSITION_MS = 350;
const EXPANDED_CLASS = 'is-expanded';

class CollectionIntroDescription extends HTMLElement {
  /** @type {HTMLElement | null} */ #content = null;
  /** @type {HTMLButtonElement | null} */ #btn = null;
  /** @type {IntersectionObserver | null} */ #visibilityObserver = null;

  connectedCallback() {
    this.#content = this.querySelector('.collection-intro-description__content');
    this.#btn = this.querySelector('.collection-intro-description__toggle');
    if (!this.#content || !this.#btn) return;

    this.#btn.addEventListener('click', this.#onToggleClick);

    // The collection intro is initially hidden inside results-list (Algolia
    // moves it into the visible grid on first render). Defer the overflow
    // check until the element actually becomes visible — otherwise both
    // scrollHeight and clientHeight read 0 and the button stays hidden.
    this.#visibilityObserver = new IntersectionObserver(this.#onVisibilityChange);
    this.#visibilityObserver.observe(this);

    document.fonts?.ready.then(this.#updateButton);
    window.addEventListener('resize', this.#updateButton);
  }

  disconnectedCallback() {
    this.#visibilityObserver?.disconnect();
    window.removeEventListener('resize', this.#updateButton);
  }

  #isOverflowing() {
    if (!this.#content) return false;
    return Array.from(this.#content.querySelectorAll('.text-block__content, .custom-liquid-block')).some(
      (el) => el.scrollHeight > el.clientHeight + 2,
    );
  }

  #updateButton = () => {
    if (!this.#btn || this.classList.contains(EXPANDED_CLASS)) return;
    this.#btn.toggleAttribute('hidden', !this.#isOverflowing());
  };

  #onVisibilityChange = (/** @type {IntersectionObserverEntry[]} */ entries) => {
    if (entries[0]?.isIntersecting) this.#updateButton();
  };

  #onToggleClick = () => {
    if (this.classList.contains(EXPANDED_CLASS)) this.#collapse();
    else this.#expand();
  };

  #expand() {
    const content = this.#content;
    const btn = this.#btn;
    if (!content || !btn) return;

    const startHeight = content.scrollHeight;
    this.classList.add(EXPANDED_CLASS);
    const endHeight = content.scrollHeight;

    btn.textContent = btn.dataset.showLess || '';
    this.#animate(startHeight, endHeight);
  }

  #collapse() {
    const content = this.#content;
    const btn = this.#btn;
    if (!content || !btn) return;

    const startHeight = content.scrollHeight;

    // Read the target collapsed height without leaving the element in that
    // visual state — line-clamp would re-apply and the children would snap
    // small before the wrapper has a chance to animate. Toggle, measure,
    // toggle back, all synchronously so no paint occurs in between.
    this.classList.remove(EXPANDED_CLASS);
    const endHeight = content.scrollHeight;
    this.classList.add(EXPANDED_CLASS);

    btn.textContent = btn.dataset.showMore || '';
    this.#animate(startHeight, endHeight, () => this.classList.remove(EXPANDED_CLASS));
  }

  /**
   * @param {number} startHeight
   * @param {number} endHeight
   * @param {() => void} [onEnd]
   */
  #animate(startHeight, endHeight, onEnd) {
    const content = this.#content;
    if (!content) return;

    content.style.transition = 'none';
    content.style.maxHeight = `${startHeight}px`;
    void content.offsetHeight; // force reflow before swapping the target
    content.style.transition = `max-height ${TRANSITION_MS}ms ease`;
    content.style.maxHeight = `${endHeight}px`;

    content.addEventListener(
      'transitionend',
      () => {
        onEnd?.();
        content.style.transition = '';
        content.style.maxHeight = '';
      },
      { once: true },
    );
  }
}

if (!customElements.get('collection-intro-description')) {
  customElements.define('collection-intro-description', CollectionIntroDescription);
}
